import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectSelectedComponent } from '../../../redux/editorSlice';
import { 
  fetchProductCategories, 
  selectProductCategories, 
  selectProductCategoriesLoading 
} from '../../../redux/WithcookieSlice';
import { usePropertyPanel } from '../PropertyPanelContext';
import { handleImageUpload as uploadImageToFridge } from '../utils/propertyUtils';
import '../PropertyPanel.css';

const QuickMenuProperties = () => {
  const selectedComponent = useSelector(selectSelectedComponent);
  const dispatch = useDispatch();
  const { handleComponentPropsChange, handleComponentPropsChangeDebounced } = usePropertyPanel();
  
  // 카테고리 목록 가져오기
  const productCategories = useSelector(selectProductCategories) || [];
  const isLoadingCategories = useSelector(selectProductCategoriesLoading);
  
  // 이미지 업로드 상태
  const [uploadingImageId, setUploadingImageId] = useState(null);
  
  // 검색어 상태
  const [searchTerm, setSearchTerm] = useState('');
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  
  // 로컬 입력 상태 (디바운싱용)
  const [localInputs, setLocalInputs] = useState({
    columnsPerRow: selectedComponent?.componentProps?.columnsPerRow || 8,
    imageSize: selectedComponent?.componentProps?.imageSize || 80,
    gap: selectedComponent?.componentProps?.gap || 16,
    allCategoryGradientStart: selectedComponent?.componentProps?.allCategoryGradientStart || '#667eea',
    allCategoryGradientEnd: selectedComponent?.componentProps?.allCategoryGradientEnd || '#764ba2',
    allCategoryTextColor: selectedComponent?.componentProps?.allCategoryTextColor || '#ffffff'
  });
  
  // 상품 카테고리 목록 가져오기
  useEffect(() => {
    dispatch(fetchProductCategories());
  }, [dispatch]);
  
  // selectedComponent가 변경될 때 로컬 입력 상태 업데이트
  useEffect(() => {
    if (selectedComponent) {
      setLocalInputs({
        columnsPerRow: selectedComponent.componentProps?.columnsPerRow || 8,
        imageSize: selectedComponent.componentProps?.imageSize || 80,
        gap: selectedComponent.componentProps?.gap || 16,
        allCategoryGradientStart: selectedComponent.componentProps?.allCategoryGradientStart || '#667eea',
        allCategoryGradientEnd: selectedComponent.componentProps?.allCategoryGradientEnd || '#764ba2',
        allCategoryTextColor: selectedComponent.componentProps?.allCategoryTextColor || '#ffffff'
      });
    }
  }, [selectedComponent?.id]); // selectedComponent의 id가 변경될 때만 실행
  
  // ESC 키로 모달 닫기
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && showCategoryPicker) {
        setShowCategoryPicker(false);
        setSearchTerm('');
      }
    };
    
    if (showCategoryPicker) {
      document.addEventListener('keydown', handleEsc);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [showCategoryPicker]);
  
  if (!selectedComponent) {
    return <div className="component-properties">컴포넌트를 선택하세요.</div>;
  }
  
  // componentProps에서 속성 추출
  const { 
    categoryItems = [],
    columnsPerRow = 8,
    showCategoryName = true,
    showAllCategory = true,
    allCategoryGradientStart = '#667eea',
    allCategoryGradientEnd = '#764ba2',
    allCategoryTextColor = '#ffffff',
    imageSize = 80,
    gap = 16,
    hoverEffect = true
  } = selectedComponent.componentProps || {};
  
  // 컴포넌트 속성 변경 핸들러 - PropertyPanelContext의 함수 사용
  const updateComponentProperty = (path, value) => {
    handleComponentPropsChange(path, value);
  };
  
  // 디바운싱이 적용된 입력 핸들러
  const handleLocalInputChange = (field, value) => {
    // 로컬 상태 즉시 업데이트 (UI 반응성)
    setLocalInputs(prev => ({
      ...prev,
      [field]: value
    }));
    
    // 디바운싱된 업데이트 (실제 컴포넌트 업데이트)
    handleComponentPropsChangeDebounced(field, value, 300);
  };
  
  // 최상위 카테고리만 필터링
  const topCategories = productCategories.filter(category => !category.parentId);
  
  // 검색어로 필터링된 카테고리
  const filteredCategories = topCategories.filter(category => 
    category.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // 카테고리가 이미 선택되었는지 확인
  const isCategorySelected = (categoryId) => {
    return categoryItems.some(item => item.categoryId === categoryId);
  };
  
  // 카테고리 토글 (선택/해제)
  const toggleCategory = (category) => {
    if (isCategorySelected(category.id)) {
      // 이미 선택된 경우 제거
      const updatedItems = categoryItems.filter(item => item.categoryId !== category.id);
      updateComponentProperty('categoryItems', updatedItems);
    } else {
      // 선택되지 않은 경우 추가
      const newItem = {
        id: `cat-${Date.now()}`,
        categoryId: category.id,
        categoryName: category.name,
        imageUrl: '',
        routingPath: '',
        visible: true
      };
      
      const updatedItems = [...categoryItems, newItem];
      updateComponentProperty('categoryItems', updatedItems);
    }
  };
  
  // 카테고리 아이템 제거
  const removeCategoryItem = (itemId) => {
    const updatedItems = categoryItems.filter(item => item.id !== itemId);
    updateComponentProperty('categoryItems', updatedItems);
  };
  
  // 이미지 업로드 핸들러 - 냉장고 서비스 사용
  const handleImageUpload = async (itemId, file) => {
    if (!file) return;
    
    setUploadingImageId(itemId);
    
    try {
      // 파일 객체를 이벤트 형태로 래핑
      const fakeEvent = {
        target: {
          files: [file]
        }
      };
      
      // 냉장고 서비스로 이미지 업로드
      const imageUrl = await uploadImageToFridge(fakeEvent);
      
      if (imageUrl) {
        // 해당 아이템의 이미지 URL 업데이트
        const updatedItems = categoryItems.map(item => 
          item.id === itemId 
            ? { ...item, imageUrl } 
            : item
        );
        
        updateComponentProperty('categoryItems', updatedItems);
      }
      
      setUploadingImageId(null);
    } catch (error) {
      console.error('이미지 업로드 실패:', error);
      alert('이미지 업로드에 실패했습니다. 다시 시도해주세요.');
      setUploadingImageId(null);
    }
  };
  
  // 이미지 제거 핸들러
  const handleImageRemove = (itemId) => {
    const updatedItems = categoryItems.map(item => 
      item.id === itemId 
        ? { ...item, imageUrl: '' } 
        : item
    );
    
    updateComponentProperty('categoryItems', updatedItems);
  };
  
  // 가시성 토글
  const toggleItemVisibility = (itemId) => {
    const updatedItems = categoryItems.map(item => 
      item.id === itemId 
        ? { ...item, visible: !item.visible } 
        : item
    );
    
    updateComponentProperty('categoryItems', updatedItems);
  };
  
  return (
    <div className="component-properties">
      {/* 카테고리 선택 */}
      <div className="property-section">
        <div className="property-section-header">
          <h3 className="property-section-title">카테고리 설정</h3>
        </div>
        
        {isLoadingCategories ? (
          <div className="loading-message">카테고리 목록을 불러오는 중...</div>
        ) : (
          <>
            {/* 카테고리 추가 버튼 */}
            <div className="property-item">
              <button
                className="category-add-button"
                onClick={() => setShowCategoryPicker(!showCategoryPicker)}
                type="button"
              >
                <i className="fas fa-plus-circle"></i>
                <span>카테고리 추가</span>
                <span className="category-add-count">
                  {categoryItems.length > 0 && `(${categoryItems.length}개 선택됨)`}
                </span>
              </button>
            </div>
            
            {/* 카테고리 선택 모달/드롭다운 */}
            {showCategoryPicker && (
              <div 
                className="category-picker-overlay"
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    setShowCategoryPicker(false);
                    setSearchTerm('');
                  }
                }}
              >
                <div className="category-picker-modal">
                  <div className="category-picker-header">
                    <h4>카테고리 선택</h4>
                    <button
                      className="category-picker-close"
                      onClick={() => {
                        setShowCategoryPicker(false);
                        setSearchTerm('');
                      }}
                      type="button"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                  
                  {/* 검색 입력창 */}
                  <div className="category-picker-search">
                    <div className="category-search-wrapper">
                      <i className="fas fa-search category-search-icon"></i>
                      <input
                        type="text"
                        className="category-search-input"
                        placeholder="카테고리 검색..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        autoFocus
                      />
                      {searchTerm && (
                        <button 
                          className="category-search-clear"
                          onClick={() => setSearchTerm('')}
                          type="button"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* 카테고리 목록 */}
                  <div className="category-picker-body">
                    <div className="category-picker-info">
                      <span>사용 가능한 카테고리</span>
                      <span className="category-count">({filteredCategories.length}개)</span>
                    </div>
                    <div className="category-chip-grid-modal">
                      {filteredCategories.length === 0 ? (
                        <div className="category-no-results">
                          검색 결과가 없습니다.
                        </div>
                      ) : (
                        filteredCategories.map(category => (
                          <button
                            key={category.id}
                            className={`category-chip ${isCategorySelected(category.id) ? 'selected' : ''}`}
                            onClick={() => toggleCategory(category)}
                            type="button"
                          >
                            {isCategorySelected(category.id) && (
                              <i className="fas fa-check category-chip-check"></i>
                            )}
                            <span>{category.name}</span>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                  
                  <div className="category-picker-footer">
                    <button
                      className="category-picker-done"
                      onClick={() => {
                        setShowCategoryPicker(false);
                        setSearchTerm('');
                      }}
                      type="button"
                    >
                      완료
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* 선택된 카테고리 목록 */}
            {categoryItems.length > 0 && (
              <div className="property-item" style={{ marginTop: '20px' }}>
                <label className="property-label">
                  선택된 카테고리 
                  <span className="category-count">({categoryItems.length}개)</span>
                </label>
                <div className="selected-category-list">
                  {categoryItems.map((item) => (
                    <div key={item.id} className="selected-category-item">
                      <div className="selected-category-content">
                        {/* 이미지 썸네일 */}
                        <div className="selected-category-image">
                          {item.imageUrl ? (
                            <img src={item.imageUrl} alt={item.categoryName} />
                          ) : (
                            <div className="selected-category-placeholder">
                              <i className="fas fa-image"></i>
                            </div>
                          )}
                        </div>
                        
                        {/* 카테고리 정보 */}
                        <div className="selected-category-info">
                          <span className="selected-category-name">{item.categoryName}</span>
                          <div className="selected-category-controls">
                            {/* 이미지 업로드 */}
                            <label className="selected-category-upload">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleImageUpload(item.id, e.target.files[0])}
                                style={{ display: 'none' }}
                              />
                              <i className="fas fa-camera"></i>
                            </label>
                            
                            {/* 표시/숨김 토글 */}
                            <button 
                              className={`selected-category-toggle ${item.visible ? 'visible' : 'hidden'}`}
                              onClick={() => toggleItemVisibility(item.id)}
                              type="button"
                            >
                              <i className={`fas ${item.visible ? 'fa-eye' : 'fa-eye-slash'}`}></i>
                            </button>
                            
                            {/* 삭제 버튼 */}
                            <button 
                              className="selected-category-remove"
                              onClick={() => removeCategoryItem(item.id)}
                              type="button"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {uploadingImageId === item.id && (
                        <div className="upload-progress">업로드 중...</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* 표시 설정 */}
      <div className="property-section">
        <h3 className="property-section-title">표시 설정</h3>
        
        <div className="property-item">
          <label className="property-label">한 줄에 표시할 개수</label>
          <input
            type="number"
            className="property-input"
            value={localInputs.columnsPerRow}
            onChange={(e) => handleLocalInputChange('columnsPerRow', Number(e.target.value))}
            min="3"
            max="12"
          />
          <small className="property-help">3~12개 사이로 설정하세요</small>
        </div>
        
        <div className="property-item">
          <label className="property-label">아이콘 크기 (px)</label>
          <input
            type="number"
            className="property-input"
            value={localInputs.imageSize}
            onChange={(e) => handleLocalInputChange('imageSize', Number(e.target.value))}
            min="40"
            max="200"
          />
        </div>
        
        <div className="property-item">
          <label className="property-label">아이템 간격 (px)</label>
          <input
            type="number"
            className="property-input"
            value={localInputs.gap}
            onChange={(e) => handleLocalInputChange('gap', Number(e.target.value))}
            min="0"
            max="50"
          />
        </div>
        
        <div className="toggle-option">
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={showCategoryName}
              onChange={(e) => updateComponentProperty('showCategoryName', e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
          <span className="toggle-label-text">카테고리명 표시</span>
        </div>
        
        <div className="toggle-option">
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={showAllCategory}
              onChange={(e) => updateComponentProperty('showAllCategory', e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
          <span className="toggle-label-text">전체 카테고리 표시</span>
        </div>
        
        {/* 전체 카테고리 그라데이션 색상 설정 */}
        {showAllCategory && (
          <>
            <div className="property-item">
              <label className="property-label">전체 카테고리 시작색 (12시 방향)</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="color"
                  className="property-color-input"
                  value={localInputs.allCategoryGradientStart}
                  onChange={(e) => handleLocalInputChange('allCategoryGradientStart', e.target.value)}
                  style={{ width: '50px', height: '30px', cursor: 'pointer' }}
                />
                <input
                  type="text"
                  className="property-input"
                  value={localInputs.allCategoryGradientStart}
                  onChange={(e) => handleLocalInputChange('allCategoryGradientStart', e.target.value)}
                  placeholder="#667eea"
                  style={{ flex: 1 }}
                />
              </div>
            </div>
            
            <div className="property-item">
              <label className="property-label">전체 카테고리 끝색 (6시 방향)</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="color"
                  className="property-color-input"
                  value={localInputs.allCategoryGradientEnd}
                  onChange={(e) => handleLocalInputChange('allCategoryGradientEnd', e.target.value)}
                  style={{ width: '50px', height: '30px', cursor: 'pointer' }}
                />
                <input
                  type="text"
                  className="property-input"
                  value={localInputs.allCategoryGradientEnd}
                  onChange={(e) => handleLocalInputChange('allCategoryGradientEnd', e.target.value)}
                  placeholder="#764ba2"
                  style={{ flex: 1 }}
                />
              </div>
            </div>
            
            <div className="property-item">
              <label className="property-label">전체 카테고리 텍스트 색상</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="color"
                  className="property-color-input"
                  value={localInputs.allCategoryTextColor}
                  onChange={(e) => handleLocalInputChange('allCategoryTextColor', e.target.value)}
                  style={{ width: '50px', height: '30px', cursor: 'pointer' }}
                />
                <input
                  type="text"
                  className="property-input"
                  value={localInputs.allCategoryTextColor}
                  onChange={(e) => handleLocalInputChange('allCategoryTextColor', e.target.value)}
                  placeholder="#ffffff"
                  style={{ flex: 1 }}
                />
              </div>
            </div>
            
            <div className="property-item">
              <small className="property-help">
                같은 색상을 선택하면 단색 배경이 됩니다. 
                다른 색상을 선택하면 그라데이션이 적용됩니다.
              </small>
            </div>
          </>
        )}
        
        <div className="toggle-option">
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={hoverEffect}
              onChange={(e) => updateComponentProperty('hoverEffect', e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
          <span className="toggle-label-text">호버 효과</span>
        </div>
      </div>
    </div>
  );
};

export default QuickMenuProperties;